import React, { useEffect, useState, useRef } from 'react';
import {
 collection,
 query,
 where,
 onSnapshot,
 doc,
 updateDoc,
 addDoc,
 deleteDoc,
 writeBatch,
 Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import { Service, ServiceCategory } from '../../types';
import { toast } from '@/lib/toast';
import { Upload, X, Loader2, GripVertical, ArrowUpDown } from 'lucide-react';
import {
 DndContext,
 closestCenter,
 KeyboardSensor,
 PointerSensor,
 useSensor,
 useSensors,
 type DragEndEvent,
} from '@dnd-kit/core';
import {
 arrayMove,
 SortableContext,
 sortableKeyboardCoordinates,
 useSortable,
 verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface EditingService extends Partial<Service> {
 serviceId?: string;
 isNew?: boolean;
}

const serviceCategories: { value: ServiceCategory; label: string }[] = [
 { value: 'haircut', label: 'Haircut' },
 { value: 'beard', label: 'Beard' },
 { value: 'combo', label: 'Combo' },
 { value: 'color', label: 'Color' },
 { value: 'styling', label: 'Styling' },
 { value: 'treatment', label: 'Treatment' },
 { value: 'other', label: 'Other' },
];

// Sortable item for drag-and-drop reordering
function SortableServiceItem({ service, formatPrice }: { service: Service; formatPrice: (p: number) => string }) {
 const {
 attributes,
 listeners,
 setNodeRef,
 transform,
 transition,
 isDragging,
 } = useSortable({ id: service.serviceId });

 const style = {
 transform: CSS.Transform.toString(transform),
 transition,
 opacity: isDragging ? 0.5 : 1,
 };

 return (
 <div
 ref={setNodeRef}
 style={style}
 className={`flex items-center gap-4 bg-surface border border-hairline rounded-lg p-4 ${
 isDragging ? 'shadow-lg ring-2 ring-info' : 'hover:shadow-md'
 } transition-shadow`}
 >
 <button
 {...attributes}
 {...listeners}
 className="cursor-grab active:cursor-grabbing p-1 text-ink-subtle hover:text-ink touch-none"
 aria-label="Drag to reorder"
 >
 <GripVertical className="w-5 h-5" />
 </button>
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2">
 <h4 className="font-semibold text-ink truncate">{service.name}</h4>
 <span className="px-2 py-0.5 text-xs bg-info/10 text-info rounded capitalize flex-shrink-0">
 {service.category}
 </span>
 {!service.isActive && (
 <span className="px-2 py-0.5 text-xs bg-surface-2 text-ink-muted rounded flex-shrink-0">
 Inactive
 </span>
 )}
 </div>
 </div>
 <div className="text-right flex-shrink-0">
 <p className="font-semibold text-ink">{formatPrice(service.price)} EUR</p>
 <p className="text-xs text-ink-subtle">{service.duration} min</p>
 </div>
 </div>
 );
}

interface Props {
 selectedFranchiseId: string;
 selectedBranchId: string;
}

const ServicesManagement: React.FC<Props> = ({ selectedFranchiseId, selectedBranchId }) => {
 const [services, setServices] = useState<Service[]>([]);
 const [loading, setLoading] = useState(true);
 const [editingService, setEditingService] = useState<EditingService | null>(null);
 const [searchTerm, setSearchTerm] = useState('');
 const [filterCategory, setFilterCategory] = useState<ServiceCategory | 'all'>('all');
 const [uploadingImage, setUploadingImage] = useState(false);
 const [imagePreview, setImagePreview] = useState<string | null>(null);
 const fileInputRef = useRef<HTMLInputElement>(null);
 const [reorderMode, setReorderMode] = useState(false);
 const [orderedServices, setOrderedServices] = useState<Service[]>([]);
 const [savingOrder, setSavingOrder] = useState(false);

 // Sensors for drag-and-drop
 const sensors = useSensors(
 useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
 useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
 );

 useEffect(() => {
 let servicesQuery = query(collection(db, 'services'));

 // Filter by franchiseId if a specific franchise is selected
 if (selectedFranchiseId !== 'all') {
 servicesQuery = query(servicesQuery, where('franchiseId', '==', selectedFranchiseId));
 }

 // NOTE: We intentionally DO NOT filter by branchId here
 // This allows all services from a franchise to be visible to all its branches
 // (e.g., all Monkey Barbershop branches see all Monkey Barbershop services)

 const unsubscribe = onSnapshot(servicesQuery, (snapshot) => {
 const servicesList = snapshot.docs.map((doc) => ({
 serviceId: doc.id,
 ...doc.data(),
 })) as Service[];

 // Sort by displayOrder, then by name as fallback
 servicesList.sort((a, b) => {
 const orderA = a.displayOrder ?? 999;
 const orderB = b.displayOrder ?? 999;
 if (orderA !== orderB) return orderA - orderB;
 return a.name.localeCompare(b.name);
 });

 setServices(servicesList);
 setLoading(false);
 });

 return () => unsubscribe();
 }, [selectedFranchiseId, selectedBranchId]);

 const handleEdit = (service: Service) => {
 setEditingService({
 ...service,
 isNew: false,
 });
 setImagePreview(service.imageUrl || null);
 };

 const handleCreate = () => {
 setEditingService({
 isNew: true,
 name: '',
 description: '',
 duration: 30,
 price: 0,
 currency: 'EUR',
 category: 'haircut',
 isActive: true,
 franchiseId: selectedFranchiseId !== 'all' ? selectedFranchiseId : 'default',
 branchId: '',
 imageUrl: '',
 });
 setImagePreview(null);
 };

 const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
 const file = event.target.files?.[0];
 if (!file) return;

 // Validate file type
 if (!file.type.startsWith('image/')) {
 toast.error('Por favor selecciona un archivo de imagen válido');
 return;
 }

 // Validate file size (max 5MB)
 if (file.size > 5 * 1024 * 1024) {
 toast.error('La imagen debe ser menor a 5MB');
 return;
 }

 setUploadingImage(true);

 try {
 // Create unique filename
 const timestamp = Date.now();
 const franchiseId = editingService?.franchiseId || selectedFranchiseId || 'default';
 const filename = `services/${franchiseId}/${timestamp}_${file.name}`;
 const storageRef = ref(storage, filename);
 const uploadTask = uploadBytesResumable(storageRef, file);

 uploadTask.on('state_changed',
 () => {
 // Progress handling (optional - could add progress UI)
 },
 (error) => {
 console.error('Error uploading image:', error);
 toast.error('Error al subir la imagen');
 setUploadingImage(false);
 },
 async () => {
 // Get download URL
 const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

 // Update editing service with imageUrl
 setEditingService(prev => prev ? { ...prev, imageUrl: downloadURL } : null);
 setImagePreview(downloadURL);

 toast.success('Imagen subida correctamente');
 setUploadingImage(false);
 }
 );
 } catch (error) {
 console.error('Error in image upload:', error);
 toast.error('Error al subir la imagen');
 setUploadingImage(false);
 }
 };

 const handleRemoveImage = () => {
 setEditingService(prev => prev ? { ...prev, imageUrl: '' } : null);
 setImagePreview(null);
 if (fileInputRef.current) {
 fileInputRef.current.value = '';
 }
 toast.success('Imagen eliminada');
 };

 const handleSave = async () => {
 if (!editingService) return;

 // Validation
 if (!editingService.name || editingService.name.trim() === '') {
 toast.error('Service name is required');
 return;
 }

 if (!editingService.duration || editingService.duration <= 0) {
 toast.error('Duration must be greater than 0');
 return;
 }

 if (editingService.price === undefined || editingService.price < 0) {
 toast.error('Price must be 0 or greater');
 return;
 }

 try {
 if (editingService.isNew) {
 // Create new service - place at end of current list
 const maxOrder = services.reduce((max, s) => Math.max(max, s.displayOrder ?? 0), 0);
 await addDoc(collection(db, 'services'), {
 franchiseId: editingService.franchiseId,
 branchId: editingService.branchId,
 name: editingService.name.trim(),
 description: editingService.description?.trim() || '',
 duration: editingService.duration,
 price: editingService.price,
 currency: editingService.currency || 'EUR',
 category: editingService.category,
 imageUrl: editingService.imageUrl || '',
 isActive: editingService.isActive ?? true,
 displayOrder: maxOrder + 1,
 createdAt: Timestamp.now(),
 updatedAt: Timestamp.now(),
 });
 toast.success('Service created successfully');
 } else {
 // Update existing service
 const serviceRef = doc(db, 'services', editingService.serviceId!);
 await updateDoc(serviceRef, {
 name: editingService.name.trim(),
 description: editingService.description?.trim() || '',
 duration: editingService.duration,
 price: editingService.price,
 currency: editingService.currency || 'EUR',
 category: editingService.category,
 imageUrl: editingService.imageUrl || '',
 isActive: editingService.isActive,
 updatedAt: Timestamp.now(),
 });
 toast.success('Service updated successfully');
 }

 setEditingService(null);
 setImagePreview(null);
 if (fileInputRef.current) {
 fileInputRef.current.value = '';
 }
 } catch (error) {
 console.error('Error saving service:', error);
 toast.error('Failed to save service. Please try again.');
 }
 };

 const handleDelete = async (serviceId: string) => {
 try {
 await deleteDoc(doc(db, 'services', serviceId));
 toast.success('Service deleted successfully');
 } catch (error) {
 console.error('Error deleting service:', error);
 toast.error('Failed to delete service. Please try again.');
 }
 };

 const handleToggleActive = async (serviceId: string, currentStatus: boolean) => {
 try {
 const serviceRef = doc(db, 'services', serviceId);
 await updateDoc(serviceRef, {
 isActive: !currentStatus,
 updatedAt: Timestamp.now(),
 });
 toast.success(`Service ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
 } catch (error) {
 console.error('Error toggling service status:', error);
 toast.error('Failed to update service status. Please try again.');
 }
 };

 const handleEnterReorderMode = () => {
 setOrderedServices([...services]);
 setReorderMode(true);
 };

 const handleDragEnd = (event: DragEndEvent) => {
 const { active, over } = event;
 if (!over || active.id === over.id) return;

 setOrderedServices((items) => {
 const oldIndex = items.findIndex((s) => s.serviceId === active.id);
 const newIndex = items.findIndex((s) => s.serviceId === over.id);
 return arrayMove(items, oldIndex, newIndex);
 });
 };

 const handleSaveOrder = async () => {
 setSavingOrder(true);
 try {
 const batch = writeBatch(db);
 orderedServices.forEach((service, index) => {
 const serviceRef = doc(db, 'services', service.serviceId);
 batch.update(serviceRef, {
 displayOrder: index,
 updatedAt: Timestamp.now(),
 });
 });
 await batch.commit();
 toast.success('Orden de servicios guardado');
 setReorderMode(false);
 } catch (error) {
 console.error('Error saving service order:', error);
 toast.error('Error al guardar el orden');
 } finally {
 setSavingOrder(false);
 }
 };

 const handleCancelReorder = () => {
 setReorderMode(false);
 setOrderedServices([]);
 };

 const formatPrice = (price: number) => {
 return (price / 100).toFixed(2);
 };

 const filteredServices = services.filter((service) => {
 const matchesSearch =
 service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
 service.description?.toLowerCase().includes(searchTerm.toLowerCase());

 const matchesCategory = filterCategory === 'all' || service.category === filterCategory;

 return matchesSearch && matchesCategory;
 });

 if (loading) {
 return (
 <div className="flex items-center justify-center h-96">
 <div className="text-center">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-info/30 mx-auto"></div>
 <p className="mt-4 text-ink-muted">Loading services...</p>
 </div>
 </div>
 );
 }

 return (
 <div className="p-6 space-y-6">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div>
 <h2 className="text-2xl font-bold text-ink">Services Management</h2>
 <p className="text-sm text-ink-muted mt-1">Manage services, pricing, and availability</p>
 </div>
 <div className="flex gap-2">
 <button
 onClick={handleEnterReorderMode}
 disabled={reorderMode || services.length < 2}
 className="px-4 py-2 text-sm font-medium text-ink-muted bg-surface-2 rounded-lg hover:bg-surface-3 transition-colors disabled:opacity-50 flex items-center gap-2"
 >
 <ArrowUpDown className="w-4 h-4" />
 Reordenar
 </button>
 <button
 onClick={handleCreate}
 disabled={reorderMode}
 className="px-4 py-2 text-sm font-medium text-white bg-info rounded-lg hover:bg-info/90 transition-colors disabled:opacity-50"
 >
 + Add Service
 </button>
 </div>
 </div>

 {/* Filters */}
 <div className="flex flex-col sm:flex-row gap-4">
 <input
 type="text"
 placeholder="Search services..."
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 className="flex-1 px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink placeholder:text-ink-subtle"
 />
 <select
 value={filterCategory}
 onChange={(e) => setFilterCategory(e.target.value as ServiceCategory | 'all')}
 className="px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
 >
 <option value="all">All Categories</option>
 {serviceCategories.map((cat) => (
 <option key={cat.value} value={cat.value}>
 {cat.label}
 </option>
 ))}
 </select>
 </div>

 {/* Reorder Mode */}
 {reorderMode ? (
 <div className="space-y-4">
 <div className="flex items-center justify-between bg-info/10 border border-info/30 rounded-lg p-4">
 <p className="text-sm text-info font-medium">
 Arrastra los servicios para cambiar el orden. Pulsa"Guardar orden" cuando termines.
 </p>
 <div className="flex gap-2 flex-shrink-0">
 <button
 onClick={handleCancelReorder}
 disabled={savingOrder}
 className="px-4 py-2 text-sm font-medium text-ink-muted bg-surface border border-hairline rounded-lg hover:bg-surface-3 transition-colors disabled:opacity-50"
 >
 Cancelar
 </button>
 <button
 onClick={handleSaveOrder}
 disabled={savingOrder}
 className="px-4 py-2 text-sm font-medium text-white bg-info rounded-lg hover:bg-info/90 transition-colors disabled:opacity-50 flex items-center gap-2"
 >
 {savingOrder && <Loader2 className="w-4 h-4 animate-spin" />}
 Guardar orden
 </button>
 </div>
 </div>

 <DndContext
 sensors={sensors}
 collisionDetection={closestCenter}
 onDragEnd={handleDragEnd}
 >
 <SortableContext
 items={orderedServices.map((s) => s.serviceId)}
 strategy={verticalListSortingStrategy}
 >
 <div className="space-y-2">
 {orderedServices.map((service) => (
 <SortableServiceItem
 key={service.serviceId}
 service={service}
 formatPrice={formatPrice}
 />
 ))}
 </div>
 </SortableContext>
 </DndContext>
 </div>
 ) : (
 /* Services Grid */
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
 {filteredServices.map((service) => (
 <div
 key={service.serviceId}
 className="bg-surface border border-hairline rounded-lg p-6 hover:shadow-md transition-shadow"
 >
 <div className="flex items-start justify-between mb-4">
 <div className="flex-1">
 <div className="flex items-center space-x-2 mb-2">
 <h3 className="text-lg font-semibold text-ink">{service.name}</h3>
 {!service.isActive && (
 <span className="px-2 py-1 text-xs font-medium rounded-full bg-surface-2 text-ink-muted">
 Inactive
 </span>
 )}
 </div>
 <span className="inline-block px-2 py-1 text-xs bg-info/10 text-info rounded capitalize">
 {service.category}
 </span>
 </div>
 </div>

 {service.description && (
 <p className="text-sm text-ink-muted mb-4 line-clamp-2">{service.description}</p>
 )}

 <div className="space-y-2 mb-4">
 <div className="flex items-center justify-between text-sm">
 <span className="text-ink-muted">Duration:</span>
 <span className="font-semibold text-ink">{service.duration} min</span>
 </div>
 <div className="flex items-center justify-between text-sm">
 <span className="text-ink-muted">Price:</span>
 <span className="font-semibold text-ink">
 {formatPrice(service.price)} {service.currency || 'EUR'}
 </span>
 </div>
 </div>

 <div className="flex space-x-2">
 <button
 onClick={() => handleEdit(service)}
 className="flex-1 px-3 py-2 text-sm font-medium text-info bg-info/10 rounded-lg hover:bg-info/20 transition-colors"
 >
 Edit
 </button>
 <button
 onClick={() => handleToggleActive(service.serviceId, service.isActive)}
 className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
 service.isActive
 ? 'text-accent bg-accent/10 hover:bg-accent/20'
 : 'text-success bg-success/10 hover:bg-success/20'
 }`}
 >
 {service.isActive ? 'Disable' : 'Enable'}
 </button>
 <button
 onClick={() => handleDelete(service.serviceId)}
 className="px-3 py-2 text-sm font-medium text-danger bg-danger/10 rounded-lg hover:bg-danger/20 transition-colors"
 >
 Delete
 </button>
 </div>
 </div>
 ))}

 {filteredServices.length === 0 && (
 <div className="col-span-full text-center py-12 text-ink-subtle">
 <p>No services found</p>
 </div>
 )}
 </div>
 )}

 {/* Edit/Create Modal */}
 {editingService && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
 <div className="bg-surface rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
 <div className="p-6 border-b border-hairline">
 <h3 className="text-xl font-semibold text-ink">
 {editingService.isNew ? 'Create New Service' : 'Edit Service'}
 </h3>
 </div>
 <div className="p-6 space-y-4">
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1">
 Service Name *
 </label>
 <input
 type="text"
 value={editingService.name || ''}
 onChange={(e) => setEditingService({ ...editingService, name: e.target.value })}
 className="w-full px-3 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
 required
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1">
 Description
 </label>
 <textarea
 value={editingService.description || ''}
 onChange={(e) =>
 setEditingService({ ...editingService, description: e.target.value })
 }
 rows={3}
 className="w-full px-3 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
 />
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1">
 Duration (minutes) *
 </label>
 <input
 type="number"
 value={editingService.duration || 0}
 onChange={(e) =>
 setEditingService({ ...editingService, duration: parseInt(e.target.value) })
 }
 min="1"
 className="w-full px-3 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
 required
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1">
 Price (cents) *
 </label>
 <input
 type="number"
 value={editingService.price || 0}
 onChange={(e) =>
 setEditingService({ ...editingService, price: parseInt(e.target.value) })
 }
 min="0"
 className="w-full px-3 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
 required
 />
 <p className="text-xs text-ink-subtle mt-1">
 Display: {formatPrice(editingService.price || 0)} EUR
 </p>
 </div>
 </div>

 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1">Category *</label>
 <select
 value={editingService.category || 'haircut'}
 onChange={(e) =>
 setEditingService({
 ...editingService,
 category: e.target.value as ServiceCategory,
 })
 }
 className="w-full px-3 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
 required
 >
 {serviceCategories.map((cat) => (
 <option key={cat.value} value={cat.value}>
 {cat.label}
 </option>
 ))}
 </select>
 </div>

 <div>
 <label className="block text-sm font-medium text-ink-muted mb-2">
 Imagen del Servicio
 </label>

 {imagePreview ? (
 <div className="space-y-3">
 <div className="relative w-full h-48 rounded-lg overflow-hidden border-2 border-hairline">
 <img
 src={imagePreview}
 alt="Vista previa del servicio"
 className="w-full h-full object-cover"
 />
 </div>
 <div className="flex gap-2">
 <button
 type="button"
 onClick={() => fileInputRef.current?.click()}
 disabled={uploadingImage}
 className="flex-1 px-4 py-2 bg-info text-white rounded-lg hover:bg-info/90 transition disabled:opacity-50 flex items-center justify-center gap-2"
 >
 {uploadingImage ? (
 <>
 <Loader2 className="w-4 h-4 animate-spin" />
 <span>Subiendo...</span>
 </>
 ) : (
 <>
 <Upload className="w-4 h-4" />
 <span>Cambiar Imagen</span>
 </>
 )}
 </button>
 <button
 type="button"
 onClick={handleRemoveImage}
 disabled={uploadingImage}
 className="px-4 py-2 bg-danger text-white rounded-lg hover:bg-danger/90 transition disabled:opacity-50 flex items-center justify-center"
 >
 <X className="w-4 h-4" />
 </button>
 </div>
 </div>
 ) : (
 <div>
 <button
 type="button"
 onClick={() => fileInputRef.current?.click()}
 disabled={uploadingImage}
 className="w-full px-4 py-4 border-2 border-dashed border-hairline rounded-lg hover:border-info/30 dark:hover:border-info/30 transition flex items-center justify-center gap-2 text-ink-muted hover:text-info disabled:opacity-50 bg-surface"
 >
 {uploadingImage ? (
 <>
 <Loader2 className="w-5 h-5 animate-spin" />
 <span>Subiendo imagen...</span>
 </>
 ) : (
 <>
 <Upload className="w-5 h-5" />
 <span>Subir Imagen del Servicio</span>
 </>
 )}
 </button>
 <p className="text-xs text-ink-subtle mt-2">
 Recomendado: 800x600px o mayor. Máx 5MB. JPG, PNG o WebP.
 </p>
 </div>
 )}

 <input
 ref={fileInputRef}
 type="file"
 accept="image/*"
 onChange={handleImageUpload}
 className="hidden"
 disabled={uploadingImage}
 />
 </div>

 <div className="flex items-center">
 <input
 type="checkbox"
 id="isActive"
 checked={editingService.isActive ?? true}
 onChange={(e) =>
 setEditingService({ ...editingService, isActive: e.target.checked })
 }
 className="w-4 h-4 text-info border-hairline rounded focus:ring-focus"
 />
 <label htmlFor="isActive" className="ml-2 text-sm font-medium text-ink-muted">
 Active
 </label>
 </div>
 </div>
 <div className="p-6 border-t border-hairline flex justify-end space-x-3">
 <button
 onClick={() => {
 setEditingService(null);
 setImagePreview(null);
 if (fileInputRef.current) {
 fileInputRef.current.value = '';
 }
 }}
 className="px-4 py-2 text-sm font-medium text-ink-muted bg-surface-2 rounded-lg hover:bg-surface-3 transition-colors"
 >
 Cancel
 </button>
 <button
 onClick={handleSave}
 className="px-4 py-2 text-sm font-medium text-white bg-info rounded-lg hover:bg-info/90 transition-colors"
 >
 {editingService.isNew ? 'Create Service' : 'Save Changes'}
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
};

export default ServicesManagement;
